import React, { useEffect, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMapEvents, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix for default marker icons in react-leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png')
});

/**
 * Component to handle map click events
 */
const MapClickHandler = ({ onMapClick, readOnly }) => {
  useMapEvents({
    click: (e) => {
      if (!readOnly && onMapClick) {
        onMapClick({
          lat: e.latlng.lat,
          lng: e.latlng.lng
        });
      }
    }
  });
  return null;
};

/**
 * Component to update map center when it changes
 */
const MapCenterUpdater = ({ center }) => {
  const map = useMap();
  const prevCenterRef = useRef(center);

  useEffect(() => {
    if (center &&
        (prevCenterRef.current.lat !== center.lat ||
         prevCenterRef.current.lng !== center.lng)) {
      map.panTo([center.lat, center.lng]);
      prevCenterRef.current = center;
    }
  }, [center, map]);

  return null;
};

/**
 * Leaflet Map display component with marker support
 *
 * @param {Object} props
 * @param {Object} props.center - Map center { lat, lng }
 * @param {number} props.zoom - Zoom level (default 12)
 * @param {string} props.height - Map height (default '400px')
 * @param {Function} props.onMapClick - Callback when map is clicked
 * @param {Array} props.markers - Array of marker data
 * @param {boolean} props.readOnly - Disable interactions
 */
const MapView = ({
  center,
  zoom = 12,
  height = '400px',
  onMapClick,
  markers = [],
  readOnly = false
}) => {
  // Custom marker icons
  const selectedIcon = new L.Icon({
    iconUrl: 'data:image/svg+xml;base64,' + btoa(`
      <svg width="40" height="40" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg">
        <circle cx="20" cy="20" r="10" fill="#FF7426" stroke="#ffffff" stroke-width="2"/>
      </svg>
    `),
    iconSize: [40, 40],
    iconAnchor: [20, 20],
    popupAnchor: [0, -20]
  });

  const teacherIcon = new L.Icon({
    iconUrl: 'data:image/svg+xml;base64,' + btoa(`
      <svg width="32" height="32" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg">
        <circle cx="16" cy="16" r="8" fill="#3B82F6" fill-opacity="0.8" stroke="#ffffff" stroke-width="2"/>
      </svg>
    `),
    iconSize: [32, 32],
    iconAnchor: [16, 16],
    popupAnchor: [0, -16]
  });

  const getMarkerIcon = (type) => {
    if (type === 'selected') return selectedIcon;
    if (type === 'teacher') return teacherIcon;
    return undefined; // Default Leaflet marker
  };

  return (
    <div
      style={{
        width: '100%',
        height,
        borderRadius: 12,
        overflow: 'hidden',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
      }}
    >
      <MapContainer
        center={[center.lat, center.lng]}
        zoom={zoom}
        style={{ width: '100%', height: '100%' }}
        zoomControl={true}
        scrollWheelZoom={!readOnly}
      >
        {/* OpenStreetMap tiles */}
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />

        {/* Map click handler */}
        <MapClickHandler onMapClick={onMapClick} readOnly={readOnly} />

        {/* Center updater */}
        <MapCenterUpdater center={center} />

        {/* Markers */}
        {markers.map((markerData, index) => (
          <Marker
            key={index}
            position={[markerData.position.lat, markerData.position.lng]}
            icon={getMarkerIcon(markerData.type)}
          >
            {markerData.label && (
              <Popup>
                <div style={{ padding: 8, fontWeight: 600, color: '#1e293b' }}>
                  {markerData.label}
                </div>
              </Popup>
            )}
          </Marker>
        ))}
      </MapContainer>
    </div>
  );
};

export default MapView;
